export const BACKEND_APIS = {
	// Backend APIs
	SKU_DATA: '/backend/sku',
	SKU_MAPPING: '/backend/sku-mapping',
	SCHEDULER: '/backend/scheduler',
	ADMIN: '/api/admin',
	ANALYTICS: '/api/analytics',
	CATALOGUE: '/api/catalogue',
	DIGITAL_SHELF: '/api/digital-shelf',
	DIGITAL_SHELF_SELLER: '/api/digital-shelf-seller',
	HOME: '/api/home',
	MARKETPLACES: '/api/marketplaces',
	MASTER_DATA: '/api/master-data',
	PRICING: '/api/pricing',
	BANNERS: '/api/banners',
	PRODUCT_PAGE: '/api/product-page',
	REPRICING: '/api/repricing',
	SESSION: '/api/session',
	SHARE_OF_SEARCH: '/api/share-of-search',
	STOCK_ASSORTMENT: '/api/stock-assortment',
	SKU_DETAILS: '/api/sku-details',
	REPORTS: '/api/reports',
	CUSTOM_DASHBOARD: '/api/custom-dash',
	MANAGEMENT: '/api/management',
	MONITORING: '/api/monitoring',
	BACK_BANNERS_V2: '/backend/v2/sov',
	AI_INSIGHTS: '/api/ai-insights',
}

// dipi-webback-v2 (FastAPI + HeatWave) is served under its own prefix so it can run alongside the
// legacy webback and the front can route by path. See
// dipi-webback-v2/plans/869e5pfw2--general.md (D3).
//
// Endpoints are migrated **one at a time**, and a call site opts in by building its url with this
// prefix instead of `BACKEND_APIS`. Deliberately no central list of migrated routes: it would be
// shared state that every module's migration branch has to edit, and it buys nothing — removing an
// entry from a list is a code change and a redeploy, exactly like changing the url.
// **To see what is on v2:** `grep -rn "WEBBACK_V2_API" src/`.
export const WEBBACK_V2_API = '/api/v2'

const __CATALOGUE_PATH = '/catalogue'
const __CATALOGUE_ID = 'catalogue'
export const CATALOGUE_ROUTES = {
	ACCESS_KEY: 'catalogue',
	PATH: `${__CATALOGUE_PATH}`,
	TITLE: 'Catalogue Analyzer',
	SUBTITLE: 'Visualize and optimize your product catalog with detailed insights',

	ANALYZER_PATH: `${__CATALOGUE_PATH}/analyzer`,

	DETAILS_ID: `${__CATALOGUE_ID}-details`,
	DETAILS_PATH: `${__CATALOGUE_PATH}/details`,
	DETAILS_TITLE: 'Catalogue Details',
	DETAILS_SUBTITLE: 'List of catalog entries and  product mapping info',

	LEGACY_ID: `${__CATALOGUE_ID}-legacy`,
	LEGACY_PATH: `${__CATALOGUE_PATH}/legacy`,
}

const __DIGITAL_SHELF_PATH = '/digital-shelf'
const __DIGITAL_SHELF_ID = 'digitalShelf'
export const DIGITAL_SHELF_ROUTES = {
	ACCESS_KEY: 'digitalShelf',
	PATH: __DIGITAL_SHELF_PATH,
	TITLE: 'Digital Shelf',
	SUBTITLE: 'Aggregate and Analyze KPI Compliance for Enhanced Product Performance',

	OVERVIEW_ID: `${__DIGITAL_SHELF_ID}-overview`,
	OVERVIEW_PATH: `${__DIGITAL_SHELF_PATH}/overview`,
	OVERVIEW_SUBTITLE: '',

	SHARE_OF_SEARCH_PATH: `${__DIGITAL_SHELF_PATH}/share-of-search`,
	SHARE_OF_SEARCH_SUBTITLE: 'Share of search',

	SHARE_OF_BROWSE_PATH: `${__DIGITAL_SHELF_PATH}/share-of-browse`,
	SHARE_OF_BROWSE_SUBTITLE: 'Share of browse',

	PRODUCT_PAGES_PATH: `${__DIGITAL_SHELF_PATH}/product-pages`,
	PRODUCT_PAGES_SUBTITLE: 'Product page details. Detailed analysis of each metric at SKU level',

	KEYWORD_PATH: `${__DIGITAL_SHELF_PATH}/keyword-details`,
	KEYWORD_SUBTITLE: 'Keyword Details. Analyzing product visibility in keyword search',

	SECTION_SHARE_PATH: `${__DIGITAL_SHELF_PATH}/section-share`,
	SECTION_SHARE_TITLE: 'Section Share %',
	SECTION_SHARE_SUBTITLE: '',

	SECTION_PATH: `${__DIGITAL_SHELF_PATH}/section-details`,
	SECTION_SUBTITLE: 'Section Details',

	KEYWORD_SHARE_PATH: `${__DIGITAL_SHELF_PATH}/keyword-share`,
	KEYWORD_SHARE_TITLE: 'Keyword Share %',
	KEYWORD_SHARE_SUBTITLE: ''
}

const __SHARE_OF_BROWSE_PATH = '/share-of-browse'
export const SHARE_OF_BROWSE_ROUTES = {
	ACCESS_KEY: 'shareOfBrowse',
	PATH: __SHARE_OF_BROWSE_PATH,
	TITLE: 'Share of Browse',
	SUBTITLE: 'Example test for module Share of Browse',

	DETAILS_PATH: `${__SHARE_OF_BROWSE_PATH}/section-details`,
	DETAILS_TITLE: 'Share of Browse Details',
	DETAILS_SUBTITLE: 'Analyzing product visibility in section search',

	SHARE_PATH: `${__SHARE_OF_BROWSE_PATH}/share`,
	SHARE_TITLE: 'Share of Browse Share',
	SHARE_SUBTITLE: ''
}

const __SHARE_OF_SEARCH_PATH = '/share-of-search'
const __SHARE_OF_SEARCH_ID = 'shareOfSearch'
export const SHARE_OF_SEARCH_ROUTES = {
	ACCESS_KEY: 'shareOfSearch',
	PATH: __SHARE_OF_SEARCH_PATH,
	TITLE: 'Share of Search',
	SUBTITLE: "Analyze Your Brand's Visibility and Search Performance in the Market",

	DETAILS_ID: `${__SHARE_OF_SEARCH_ID}-keywordDetails`,
	DETAILS_PATH: `${__SHARE_OF_SEARCH_PATH}/keyword-details`,
	DETAILS_TITLE: 'Share of Search Details',
	DETAILS_SUBTITLE: 'Measure Brand Visibility and SKU Positions for Predefined Keywords',

	SHARE_ID: `${__SHARE_OF_SEARCH_ID}-kpis`,
	SHARE_PATH: `${__SHARE_OF_SEARCH_PATH}/KPIs`,
	SHARE_TITLE: 'Share of Search KPIs',
	SHARE_SUBTITLE: 'High-Level Overview of SKU Positioning Across Multiple Keywords',

	STATUS_ID: `${__SHARE_OF_SEARCH_ID}-status`,
	STATUS_PATH: `${__SHARE_OF_SEARCH_PATH}/status`,
	STATUS_TITLE: 'Share of Search Status',
	STATUS_SUBTITLE: 'Keyword Status Check Across Sites and Date Range'
}

export const HOME_ROUTES = {
	TITLE: 'Home',
	HOME_URL: '/home'
}

const __MARKETPLACES_PATH = '/marketplaces'
const __MARKETPLACES_ID = 'marketplaces'
export const MARKETPLACES_ROUTES = {
	ACCESS_KEY: 'marketplaces',
	PATH: __MARKETPLACES_PATH,
	TITLE: 'Marketplaces',
	SUBTITLE: 'analyze sellers, offering and pricing trends',
	
	OVERVIEW_ID: `${__MARKETPLACES_ID}-overview`,
	OVERVIEW_PATH: `${__MARKETPLACES_PATH}/overview`,
	OVERVIEW_SUBTITLE: '',

	SELLER_DETAILS_ID: `${__MARKETPLACES_ID}-sellerDetails`,
	SELLER_DETAILS_PATH: `${__MARKETPLACES_PATH}/seller-details`,
	SELLER_DETAILS_SUBTITLE: 'Seller details',

	SELLER_DEEP_DIVE_ID: `${__MARKETPLACES_ID}-sellerDeepDive`,
	SELLER_DEEP_DIVE_PATH: `${__MARKETPLACES_PATH}/seller-deep-dive`,
	SELLER_DEEP_DIVE_SUBTITLE: 'Seller deep dive',

	OFFERS_DETAILS_ID: `${__MARKETPLACES_ID}-offersDetails`,
	OFFERS_DETAILS_PATH: `${__MARKETPLACES_PATH}/offers-details`,
	OFFERS_DETAILS_SUBTITLE: 'Offers Details',

	LBBDETAILS_ID: `${__MARKETPLACES_ID}-lbb`,
	LBBDETAILS_PATH: `${__MARKETPLACES_PATH}/lbb`,
	LBBDETAILS_SUBTITLE: 'Lost Buy Box Daily Details',

	LBBOVERVIEW_ID: `${__MARKETPLACES_ID}-lbb-overview`,
	LBBOVERVIEW_PATH: `${__MARKETPLACES_PATH}/lbb-overview`,
	LBBOVERVIEW_SUBTITLE: 'Lost Buy Box Aggregated Overview'
}

const __PRICING_ID = 'pricing'
const __PRICING_PATH = '/pricing'
export const PRICING_ROUTES = {
	ACCESS_KEY: 'pricing',
	PATH: __PRICING_PATH,
	TITLE: 'Pricing & Discount',
	TITLE_RETAILER: 'Price Dispersion',
	SUBTITLE: 'Price & Discount Evolution for SKU Analysis',
	SUBTITLE_RETAILER: 'Price Dispersion Evolution for SKU Analysis',

	OVERVIEW_ID: `${__PRICING_ID}-overview`,
	OVERVIEW_PATH: `${__PRICING_PATH}/overview`,
	OVERVIEW_TITLE: 'Pricing Overview',
	OVERVIEW_SUBTITLE: 'Overview of price dispersion, price changes, stock updates and products counts across retailers',

	CHANGES_ID: `${__PRICING_ID}-changes`,
	CHANGES_PATH: `${__PRICING_PATH}/changes`,
	CHANGES_TITLE: 'Pricing Changes',
	CHANGES_SUBTITLE: 'Monitor price changes among your competitors',

	MARKET_ANALYZER_ID: `${__PRICING_ID}-marketAnalyzer`,
	MARKET_ANALYZER_PATH: `${__PRICING_PATH}/market-analyzer`,
	MARKET_ANALYZER_TITLE: 'Market Analyzer',
	MARKET_ANALYZER_SUBTITLE: 'Optimize your market strategy with insights on pricing, stock and competition',

	PRICE_EVOLUTION_ID: `${__PRICING_ID}-priceEvolution`,
	PRICE_EVOLUTION_PATH: `${__PRICING_PATH}/price-evolution`,
	PRICE_EVOLUTION_TITLE: 'Price Evolution',
	PRICE_EVOLUTION_SUBTITLE: 'Track and analyze daily pricing, discounts, and market trends ',

	SIDE_COMPARISON_ID: `${__PRICING_ID}-sideComparison`,
	SIDE_COMPARISON_PATH: `${__PRICING_PATH}/side-comparison`,
	SIDE_COMPARISON_TITLE: 'Side by Side Comparison',
	SIDE_COMPARISON_SUBTITLE: 'Identify price gaps and competitive positioning between two sites',

}

const __REPRICING_PATH = '/repricing'
const __REPRICING_ID = 'repricing'
export const REPRICING_ROUTES = {
	ACCESS_KEY: 'repricing',
	PATH: __REPRICING_PATH,
	TITLE: 'Repricing',
	SUBTITLE: 'Price Strategy',

	FORM_ID: `${__REPRICING_ID}-kpis`,
	FORM_PATH: `${__REPRICING_PATH}/form`,
	FORM_TITLE: 'Repricing',
	FORM_SUBTITLE: 'Rule configuration'
}

const __INFOCENTER_PATH = '/info-center'
const __INFOCENTER_ID = 'infocenter'
export const INFOCENTER_ROUTES = {
	ACCESS_KEY: 'infocenter',
	PATH: __INFOCENTER_PATH,
	TITLE: 'Info center',
	SUBTITLE: "Access Our Learning Space: Platform Basics, Short 'How-to' Videos, and More",

	GENERAL_ID: `${__INFOCENTER_ID}-general`,
	GENERAL_PATH: `${__INFOCENTER_PATH}/general`,
	GENERAL_TITLE: 'General',
	GENERAL_SUBTITLE: "Access Our Learning Space: Platform Basics, Short 'How-to' Videos, and More",

	RETAILERS_TRACKED_ID: `${__INFOCENTER_ID}-retailersTracked`,
	RETAILERS_TRACKED_PATH: `${__INFOCENTER_PATH}/retailers-tracked`,
	RETAILERS_TRACKED_TITLE: 'List of Retailers Tracked',
	RETAILERS_TRACKED_SUBTITLE: 'Overview of all retailers tracked, including SKU and brand counts and extraction dates',

	KPI_GLOSSARY_ID: `${__INFOCENTER_ID}-kpiGlossary`,
	KPI_GLOSSARY_PATH: `${__INFOCENTER_PATH}/kpi-glossary`,
	KPI_GLOSSARY_TITLE: 'KPI Glossary',
	KPI_GLOSSARY_SUBTITLE: 'What each Digital Shelf KPI measures, how it is calculated and when it does not apply',
}

const IAOPTIONS_PATH = '/iaoptions'
const __IA_OPTIONS_EXTERNAL_URL = 'https://dipi-app-dot-ia-seeker.ew.r.appspot.com'
export const IAOPTIONS_ROUTES = {
	ACCESS_KEY: 'iaOptions',
	PATH: IAOPTIONS_PATH,
	TITLE: 'IA Options',

	GENERAL_PATH: `${IAOPTIONS_PATH}/general`,
	EDITION_PATH: `${IAOPTIONS_PATH}/edition`,
	VISUALIZATION_PATH: `${IAOPTIONS_PATH}/visualization`,

	GENERAL_LINK: __IA_OPTIONS_EXTERNAL_URL,
	EDITION_LINK: `${__IA_OPTIONS_EXTERNAL_URL}/editDashboard?dashboardId=52671117-0c37-4190-8787-0b9a96dfff39`,
	VISUALIZATION_LINK: `${__IA_OPTIONS_EXTERNAL_URL}/viewDashboard?dashboardId=52671117-0c37-4190-8787-0b9a96dfff39`
}

const __PRODUCT_PAGE_PATH = '/product-page'
const __PRODUCT_PAGE_ID = 'productPages'
export const PRODUCT_PAGE_ROUTES = {
	ACCESS_KEY: 'productPages',
	PATH: __PRODUCT_PAGE_PATH,
	TITLE: 'Product Pages',
	SUBTITLE: 'Evaluate and Optimize the Performance of Your Product Listings',

	KPIS_ID: `${__PRODUCT_PAGE_ID}-kpis`,
	KPIS_PATH: `${__PRODUCT_PAGE_PATH}/kpis`,
	KPIS_TITLE: 'Product Pages KPIs',
	KPIS_SUBTITLE: 'Analyze KPI Performance by Market or Brand for Product Pages',

	DETAILS_ID: `${__PRODUCT_PAGE_ID}-details`,
	DETAILS_PATH: `${__PRODUCT_PAGE_PATH}/details`,
	DETAILS_TITLE: 'Product Pages - Details',
	DETAILS_SUBTITLE: 'Detailed Analysis of Each Metric at SKU Level',

	IMAGE_DETAILS_ID: `${__PRODUCT_PAGE_ID}-imageDetails`,
	IMAGE_DETAILS_PATH: `${__PRODUCT_PAGE_PATH}/image-details`,
	IMAGE_DETAILS_TITLE: 'Product Pages - Images Details',
	IMAGE_DETAILS_SUBTITLE: 'Visual Comparison of Catalog and Retailer Images for Content Consistency',
}

const __STOCK_PATH = '/stock'
const __STOCK_ID = 'stock'
export const STOCK_ROUTES  = {
	ACCESS_KEY: 'stock',
	PATH: __STOCK_PATH,
	TITLE: 'Stock',
	SUBTITLE: 'Evaluate and Optimize the Performance of Your Product Listings',

	STOCK_ASSORT_ID: `${__STOCK_ID}-assortment`,
	STOCK_ASSORT_PATH: `${__STOCK_PATH}/assortment`,
	STOCK_ASSORT_PATH_NEW: `${__STOCK_PATH}/assortment-new`,
	STOCK_ASSORT_TITLE: 'Stock & Assortment Details',
	STOCK_AFFINITY_TITLE: 'Stock & Affinity Details',
	STOCK_ASSORT_SUBTITLE: 'Track Product Availability Within Selected Date Ranges',

	STOCK_CHANGES_ID: `${__STOCK_ID}-changes`,
	STOCK_CHANGES_PATH: `${__STOCK_PATH}/changes`,
	STOCK_CHANGES_TITLE: 'Stock Changes',
	STOCK_CHANGES_SUBTITLE: 'Track Changes in Product Stock Within Selected Date Ranges',

	STOCK_SHARE_ID: `${__STOCK_ID}-share`,
	STOCK_SHARE_PATH: `${__STOCK_PATH}/share`,
	STOCK_SHARE_TITLE: 'Stock Share',
	STOCK_SHARE_SUBTITLE: 'Track Changes in Product Stock in Selected Date Ranges',
}

export const SESSIONS_ROUTES = {
	AUTH_URL: '/auth',
	AUTH_TITLE: 'Auth',

	LOGIN_URL: '/login',
	LOGIN_TITLE: 'Login',

	LOGIN_HOUSE_URL: '/login-house',
	LOGIN_HOUSE_TITLE: 'Login House',

	LOGOUT_URL: '/logout',

	RETAILERS_APP_URL: 'https://retailers.dataseekers.com'
}

const __ADMIN_PATH = '/admin'
const __ADMIN_ID = 'admin'

export const ADMIN_ROUTES  = {
	ACCESS_KEY: 'admin',
	PATH: __ADMIN_PATH,
	TITLE: 'Admin',
	SUBTITLE: 'Admin Module Subtitle',

	ADMIN_REVIEW_ID: `${__ADMIN_ID}-review`,
	ADMIN_REVIEW_PATH: `${__ADMIN_PATH}/review`,
	ADMIN_REVIEW_TITLE: 'Admin Review',
	ADMIN_REVIEW_SUBTITLE: 'Products review',

	NEW_LISTED_ID: `${__ADMIN_ID}-new-listed`,
	NEW_LISTED_PATH: `${__ADMIN_PATH}/new-listed`,
	NEW_LISTED_TITLE: 'New Listed Products',
	NEW_LISTED_SUBTITLE: 'List of raw products first seen in the selected period',

	SCRAPING_DATA_REVIEW_ID: `${__ADMIN_ID}-scraping-data-review`,
	SCRAPING_DATA_REVIEW_PATH: `${__ADMIN_PATH}/scraping-data-review`,
	SCRAPING_DATA_REVIEW_TITLE: 'Scraping Data Review',
	SCRAPING_DATA_REVIEW_SUBTITLE: '',

	SKU_ID: `${__ADMIN_ID}-sku`,
	SKU_PATH: `${__ADMIN_PATH}/sku`,
	SKU_TITLE: 'Sku',
	SKU_SUBTITLE: '',

	USER_ACTIVITIES_ID: `${__ADMIN_ID}-user-activities`,
	USER_ACTIVITIES_URL: `${__ADMIN_PATH}/user-activities`,
	USER_ACTIVITIES_TITLE: `User Activities`,
}

const __ANALYTICS_PATH = '/analytic'
const __ANALYTICS_ID = 'analytic'
export const ANALYTICS_ROUTES = {
	ACCESS_KEY: 'analytic',
	PATH: `${__ANALYTICS_PATH}`,
	TITLE: 'User Scope',
	SUBTITLE: 'Retailer Data Extraction and Mapping Performance Summary',

	DETAILS_ID: `${__ANALYTICS_ID}-overview`,
	DETAILS_PATH: `${__ANALYTICS_PATH}/details`,
	DETAILS_TITLE: 'User Scope',
	DETAILS_SUBTITLE: '',

	LEGACY_PATH: `${__ANALYTICS_PATH}/legacy`
}

const __SKU_DETAILS_PATH = '/sku'
const __SKU_DETAILS_ID = 'sku'
export const SKU_DETAILS_ROUTES = {
	ACCESS_KEY: 'skuDetails',
	PATH: __SKU_DETAILS_PATH,
	TITLE: 'Sku Details',
	
	SKU_DETAILS_ID:  `${__SKU_DETAILS_ID}-details`,
	SKU_DETAILS_URL: `${__SKU_DETAILS_PATH}/details`,
	SKU_DETAILS_TITLE: 'Sku Details',
	SKU_DETAILS_SUBTITLE: 'View comprehensive details of a specific SKU: distribution, pricing, stock, and product page for a single date or date range.',

}

const __REPORTS_PATH = '/reports'
const __REPORTS_ID = 'reports'
export const REPORTS_ROUTES = {
	ACCESS_KEY: 'reports',
	PATH: __REPORTS_PATH,
	TITLE: 'Reports',
	SUBTITLE: 'Take Control and Customize Reports Generated Across Various Application Modules',

	MANAGE_ID: `${__REPORTS_ID}-manage`,
	MANAGE_PATH: `${__REPORTS_PATH}/manage`,
	MANAGE_TITLE: 'Reports',
	MANAGE_SUBTITLE: 'Manage and optimize your reports'
}

const __CUSTOM_DASHBOARD_PATH = '/custom-dashboards'
const __CUSTOM_DASHBOARD_ID = 'customDashboards'
export const CUSTOM_DASHBOARD_ROUTES = {
	ACCESS_KEY: 'customDashboards',
	PATH: __CUSTOM_DASHBOARD_PATH,
	ID: __CUSTOM_DASHBOARD_ID,
	TITLE: 'Custom Dashboards',

	EMBARGO_ID: `${__CUSTOM_DASHBOARD_ID}-embargo`,
	EMBARGO_PATH: `${__CUSTOM_DASHBOARD_PATH}/embargo`,
	EMBARGO_TITLE: 'Embargo Date Compliance',
	EMBARGO_SUBTITLE: 'Monitor and verify embargo dates across markets and products to ensure compliance before product availability on-site.',

	AMAZON_RELEASE_DATE_ID: `${__CUSTOM_DASHBOARD_ID}-amazonReleaseDate`,
	AMAZON_RELEASE_DATE_PATH: `${__CUSTOM_DASHBOARD_PATH}/amazon-release-date`,
	AMAZON_RELEASE_DATE_TITLE: 'Amazon Release Date',
	AMAZON_RELEASE_DATE_SUBTITLE: 'Compare Street and Amazon release dates to define actions if needed',

	DYNAMIC_ASSORTMENT_ID: `${__CUSTOM_DASHBOARD_ID}-dynamicAssortment`,
	DYNAMIC_ASSORTMENT_PATH: `${__CUSTOM_DASHBOARD_PATH}/dynamic-assortment`,
	DYNAMIC_ASSORTMENT_TITLE: 'Dynamic Assortment',
	DYNAMIC_ASSORTMENT_SUBTITLE: 'View the SKUs included in each site\'s scope for assortment calculation',

	AMAZON_PP_KPIS_ID: `${__CUSTOM_DASHBOARD_ID}-amazonPPKpis`,
	AMAZON_PP_KPIS_PATH: `${__CUSTOM_DASHBOARD_PATH}/amazon-pp-kpis`,
	AMAZON_PP_KPIS_TITLE: 'Amazon Product Page Kpis',
	AMAZON_PP_KPIS_SUBTITLE: '',
}

const __CUSTOM_DASHBOARD_PRIMOR_PATH = '/custom-dashboards'
const __CUSTOM_DASHBOARD_PRIMOR_ID = 'customDashboardsPrimor'
export const CUSTOM_DASHBOARD_PRIMOR_ROUTES = {
	ACCESS_KEY: 'customDashboardsPrimor',
	PATH: __CUSTOM_DASHBOARD_PRIMOR_PATH,
	ID: __CUSTOM_DASHBOARD_PRIMOR_ID,
	TITLE: 'Flash Offers',

	FLASH_OFFERS_ID: `${__CUSTOM_DASHBOARD_PRIMOR_ID}-flashOffers`,
	FLASH_OFFERS_PATH: `${__CUSTOM_DASHBOARD_PRIMOR_PATH}/flash-offers`,
	FLASH_OFFERS_TITLE: 'Flash Offers',
	FLASH_OFFERS_SUBTITLE: 'Ofertas flash diarias de Druni y Primor',
}

const __MANAGEMENT_PATH = '/management'
const __MANAGEMENT_ID = 'management'
export const MANAGEMENT_ROUTES = {
	ACCESS_KEY: 'management',
	PATH: __MANAGEMENT_PATH,
	ID: __MANAGEMENT_ID,
	TITLE: 'Management',

	CUSTOMER_CRUDS_ID: `${__MANAGEMENT_ID}-customerCruds`,
	CUSTOMER_CRUDS_PATH: `${__MANAGEMENT_PATH}/customer-cruds`,
	CUSTOMER_CRUDS_TITLE: 'Customer Cruds',

	KEYWORDS_CRUDS_ID: `${__MANAGEMENT_ID}-keywordsCrud`,
	KEYWORDS_CRUDS_PATH: `${__MANAGEMENT_PATH}/keywords-crud`,
	KEYWORDS_CRUDS_TITLE: 'Keywords Crud',

	SKU_GROUP_CRUDS_ID: `${__MANAGEMENT_ID}-skuGroupCruds`,
	SKU_GROUP_CRUDS_PATH: `${__MANAGEMENT_PATH}/sku-group-cruds`,
	SKU_GROUP_CRUDS_TITLE: 'SKU Group Cruds',

	PRICING_CONFIG_ID: `${__MANAGEMENT_ID}-pricingConfig`,
	PRICING_CONFIG_PATH: `${__MANAGEMENT_PATH}/pricing-config`,
	PRICING_CONFIG_TITLE: 'Pricing Config',
	PRICING_CONFIG_SUBTITLE: 'Reference price of a customer: its RRSP or its reference retailer'
}

const __MONITORING_PATH = '/monitoring'
const __MONITORING_ID = 'monitoring'
export const MONITORING_ROUTES = {
	ACCESS_KEY: 'monitoring',
	PATH: __MONITORING_PATH,
	TITLE: 'Price Monitoring',
	SUBTITLE: 'Multiple daily price checks',

	OVERVIEW_ID: `${__MONITORING_ID}-overview`,
	OVERVIEW_PATH: `${__MONITORING_PATH}/overview`,
	OVERVIEW_TITLE: 'Price Monitoring',
	OVERVIEW_SUBTITLE: 'Track price updates multiple times per day.',

	ADMIN_ID: `${__MONITORING_ID}-admin`,
	ADMIN_PATH: `${__MONITORING_PATH}/scheduler`,
	ADMIN_TITLE: 'Extraction Scheduler',
	ADMIN_SUBTITLE: 'Define when and how often data is collected',
}

const __BANNERS_PATH = '/banners'
const __BANNERS_ID = 'banners'
export const BANNERS_ROUTES = {
	ACCESS_KEY: 'banners',
	PATH: __BANNERS_PATH,
	TITLE: 'Banners',
	SUBTITLE: '',

	PERCENTAGE_ID: `${__BANNERS_ID}-percentage`,
	PERCENTAGE_URL: `${__BANNERS_PATH}/percentage`,
	PERCENTAGE_TITLE: 'Share of Banners %',
	PERCENTAGE_SUBTITLE: 'Overview of brand visibility across markets and retailers, focusing on promotional placements',

	TOP_BANNERS_ID: `${__BANNERS_ID}-topBanners`,
	TOP_BANNERS_PATH: `${__BANNERS_PATH}/top-banners`,
	TOP_BANNERS_TITLE: 'Top Banners',
	TOP_BANNERS_SUBTITLE: 'Overview of top-performing banner assets, grouped by their active timeframes.',

	BANNERS_DETAILS_ID: `${__BANNERS_ID}-details`,
	BANNERS_DETAILS_PATH: `${__BANNERS_PATH}/details`,
	BANNERS_DETAILS_TITLE: 'Banners Details',
	BANNERS_DETAILS_SUBTITLE: 'Detailed view of banner assets and their associated attributes across markets and retailers.',

	VIEW_SCREENSHOT_ID: `${__BANNERS_ID}-view-screenshot`,
	VIEW_SCREENSHOT_URL: `/view-screenshot/v2/sov/:idSearch`,
	VIEW_SCREENSHOT_TITLE: `Banners View Screenshot`,
}

// News — its own module, not a child of Management.
//
// The `newsManagement` module is granted per user in `DIPI_USER_MODULE`, so being an admin is
// not enough to see this in the menu: customer 0 also holds accounts that are not staff, and
// they must not be able to publish to the whole platform. The backend enforces both
// conditions (`admin/news_management/deps.py`); this key is only what hides the entry.
const __NEWS_MANAGEMENT_PATH = '/news-management'
const __NEWS_MANAGEMENT_ID = 'newsManagement'
export const NEWS_MANAGEMENT_ROUTES = {
	// ⚠️ The label is "News"; the module key stays `newsManagement`. They are not the same
	// thing: the key is what `DIPI_USER_MODULE` holds and what the backend guard reads
	// (`admin/news_management/deps.py`), so renaming it would mean a database migration and a
	// backend change. Only the menu wording was asked for.
	ACCESS_KEY: 'newsManagement',
	PATH: __NEWS_MANAGEMENT_PATH,
	ID: __NEWS_MANAGEMENT_ID,
	TITLE: 'News',
	SUBTITLE: 'In-app announcements: who sees them, when, and how many have read them',

	ANNOUNCEMENTS_ID: `${__NEWS_MANAGEMENT_ID}-announcements`,
	ANNOUNCEMENTS_PATH: `${__NEWS_MANAGEMENT_PATH}/announcements`,
	ANNOUNCEMENTS_TITLE: 'Announcements',
	ANNOUNCEMENTS_SUBTITLE: 'In-app announcements: who sees them, when, and how many have read them',
}
